import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Box, Typography, Button, Modal, TextField, Rating as MuiRating } from '@mui/material';
import { FaStar, FaStarHalfAlt, FaBuilding, FaUserTie, FaHandHoldingUsd, FaLayerGroup, FaMapMarkerAlt, FaClock, FaArrowRight } from 'react-icons/fa';
import { HiDocumentPlus } from "react-icons/hi2";
import { IoDocumentText } from "react-icons/io5";
import { MdRecycling } from "react-icons/md";
import { getAuth } from 'firebase/auth';
import { 
  getDatabase, 
  ref, 
  push,
  query, 
  orderByChild, 
  equalTo, 
  onValue, 
  get 
} from 'firebase/database';
import { app } from '../firebase';
import { toast } from 'react-toastify';
import homeBanner from '../assets/home-banner.png';
import howHireMeWorks from '../assets/how-hireme-works-image.png';
// import howHireMeWorks1 from '../assets/how-hireme-works-image-1.png';

const steps = [
  {
    id: 1,
    title: 'Creating an Account',
    description:
      'Users, whether employers or job seekers, start by signing up and creating a profile. Employers provide company details, while job seekers complete their profiles with resumes and relevant information.',
    highlight: 'Creating'
  },
  {
    id: 2,
    title: 'Posting and Searching for Jobs',
    description:
      'Employers can post detailed job listings, including job titles, responsibilities, qualifications, and benefits. Job seekers can search for jobs using filters such as job title, location, salary range, and company name to find the best matches.',
    highlight: 'Searching'
  },
  {
    id: 3,
    title: 'Application Process',
    description:
      'Job seekers can apply for jobs directly through the platform by submitting their resumes and cover letters. Employers can manage applications, sort resumes, schedule interviews, and communicate with candidates through the platform.',
    highlight: 'Process'
  },
  {
    id: 4,
    title: 'Resume Database and Search',
    description:
      'Employers can access a searchable database of candidate resumes, using filters to find suitable candidates based on location, experience, education, and skills.',
    highlight: 'Search'
  },
  {
    id: 5,
    title: 'Job Alerts and Notifications',
    description:
      'Job seekers can set up personalized job alerts to receive notifications of new job postings that match their criteria, ensuring they never miss an opportunity.',
    highlight: 'Job Alerts'
  }
];

const whatwedo = [
  {
    icon: MdRecycling,
    description:
      "Choose our time-saving and cost-effective hiring system. You get a more seamless recruiting and hiring process tailored for the Jamaican job market — saving you time and money on every hire.",
  },
  {
    icon: FaHandHoldingUsd,
    description:
      "Find ideal employees using our searchable database of hundreds of Jamaican candidates. Filter by parish, experience, and qualifications — and shortlist the right people in minutes.",
  },
  {
    icon: HiDocumentPlus,
    description:
      "Enjoy the convenience of a platform designed to level the playing field. We give all Jamaican job seekers a fair chance to showcase their skills and suitability for available positions.",
  },
  {
    icon: IoDocumentText,
    description:
      "Post a job in minutes and start receiving applications from qualified candidates across Jamaica. Review, shortlist, and connect with your next hire — all in one place.",
  },
  {
    icon: FaLayerGroup,
    description:
      "Browse Jamaica's most comprehensive job board by parish, industry, or employment type. From Kingston to Montego Bay, we connect employers and job seekers across all 14 parishes.",
  },
];

const FeatureItem = ({ feature }) => {
  return (
    <div className="rounded-[20px] bg-white relative p-6 pt-12 lg:p-12 ml-6 h-full" 
         style={{ border: '2px solid #cddd3a' }}>
      <div className="w-[74px] h-[74px] bg-white rounded-full text-[32px] inline-flex items-center justify-center mb-6 absolute left-0 top-0 -m-6" 
           style={{ color: "#cddd3a", border: '2px solid #cddd3a' }}>
        <feature.icon />
      </div>
      <p className="opacity-70">{feature.description}</p>
    </div>
  );
};

// Rating Component
const Rating = ({ rating }) => (
  <p>
    <span className="flex justify-center">
      {[...Array(5)].map((_, i) => {
        const index = i + 1;
        if (index <= Math.floor(rating)) {
          return <FaStar key={i} className="text-yellow-500 text-xl" />;
        } else if (rating > i && rating < index + 1) {
          return <FaStarHalfAlt key={i} className="text-yellow-500 text-xl" />;
        }
        return <FaStar key={i} className="text-gray-200 text-xl" />;
      })}
    </span>
  </p>
);

const Home = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [testimonials, setTestimonials] = useState([]);
  const [userType, setUserType] = useState(null);
  const [expandedReviews, setExpandedReviews] = useState(new Set());
  const [latestJobs, setLatestJobs] = useState([]);

  const auth = getAuth(app);
  const db = getDatabase(app);

  useEffect(() => {
    get(ref(db, 'jobs')).then((snapshot) => {
      if (!snapshot.exists()) return;
      const all = [];
      snapshot.forEach((empSnap) => {
        empSnap.forEach((jobSnap) => {
          const job = jobSnap.val();
          if (job.status === 'approved' || job.status === 'active') {
            all.push({ ...job, _key: jobSnap.key });
          }
        });
      });
      all.sort((a, b) =>
        (b.approvedAt || b.createdAt || 0) - (a.approvedAt || a.createdAt || 0)
      );
      setLatestJobs(all.slice(0, 6));
    }).catch(() => {});
  }, [db]);

  // Check for email verification success
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('verified') === 'true') {
      toast.success('Email verified successfully! You can now sign in to your account.', {
        autoClose: 6000
      });
      // Clean up the URL
      navigate('/', { replace: true });
    }
  }, [location.search, navigate]);

  const checkUserType = React.useCallback(() => {
    const user = auth.currentUser;
    if (user) {
      try {
        const employerRef = ref(db, `employers/${user.uid}`);
        const candidateRef = ref(db, `candidates/${user.uid}`);
        
        onValue(employerRef, (snapshot) => {
          if (snapshot.exists()) {
            setUserType('employer');
          }
        });

        onValue(candidateRef, (snapshot) => {
          if (snapshot.exists()) {
            setUserType('candidate');
          }
        });
      } catch (error) {
        console.error('Error checking user type:', error);
      }
    }
  }, [auth, db]);

  const toggleReviewExpansion = (reviewId) => {
  setExpandedReviews(prev => {
    const newSet = new Set(prev);
    if (newSet.has(reviewId)) {
      newSet.delete(reviewId);
    } else {
      newSet.add(reviewId);
    }
    return newSet;
  });
};

  const fetchApprovedReviews = React.useCallback(() => {
    try {
      const approvedReviewsRef = query(
        ref(db, 'reviews'), 
        orderByChild('status'),
        equalTo('approved')
      );
  
      return onValue(approvedReviewsRef, async (snapshot) => {
        if (!snapshot.exists()) {
          setTestimonials([]);
          return;
        }
  
        try {
          const reviewsData = snapshot.val();
          const reviewsArray = [];
  
          for (const [id, review] of Object.entries(reviewsData)) {
            try {
              const userRef = ref(db, `${review.userType}s/${review.userId}`);
              const userSnap = await get(userRef);
              const userData = userSnap.exists() ? userSnap.val() : null;
  
              reviewsArray.push({
                id,
                ...review,
                firstName: userData?.firstName || userData?.profile?.firstName || 'Anonymous',
                lastName: userData?.lastName || userData?.profile?.lastName || 'User',
                companyName: userData?.profile?.companyName || (review.userType === 'employer' ? 'Company' : null)
              });
            } catch (error) {
              console.error(`Error fetching user data for review:`, error);
              reviewsArray.push({
                id,
                ...review,
                firstName: 'Anonymous',
                lastName: 'User',
                companyName: review.userType === 'employer' ? 'Company' : null
              });
            }
          }
  
          setTestimonials(reviewsArray);
        } catch (error) {
          console.error('Error processing reviews:', error);
          setTestimonials([]);
        }
      });
    } catch (error) {
      console.error('Error setting up reviews listener:', error);
      setTestimonials([]);
      return () => {};
    }
  }, [db]);

  useEffect(() => {
    fetchApprovedReviews();
    checkUserType();
  }, [fetchApprovedReviews, checkUserType]);

  const handleSubmitReview = async () => {
    if (!auth.currentUser) {
      toast.error('Please sign in to submit a review');
      return;
    }

    if (!reviewComment || reviewRating === 0) {
      toast.error('Please provide both a rating and comment');
      return;
    }

    try {
      await push(ref(db, 'reviews'), {
        userId: auth.currentUser.uid,
        userType: userType,
        rating: reviewRating,
        comment: reviewComment,
        isAnonymous: isAnonymous,
        status: 'pending',
        createdAt: Date.now()
      });

      toast.success('Review submitted successfully and pending approval');
      setIsModalOpen(false);
      setReviewRating(0);
      setReviewComment('');
      setIsAnonymous(false);
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error('Error submitting review');
    }
  };

  const handleGetHiredClick = () => {
    navigate("/candidate-registration");
  };

  const handleFindTalentClick = () => {
    navigate("/employer-registration");
  };

  const handleTemporaryWorkersClick = () => {
    navigate("/temporary-workers/apply");
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'HireMeJA',
    alternateName: 'HireMe Jamaica',
    url: 'https://hiremeja.com',
    logo: 'https://hiremeja.com/logo512.png',
    description: "Jamaica's premier online job board connecting Jamaican job seekers with employers across all 14 parishes. Post jobs, find candidates, and build careers in Jamaica.",
    foundingDate: '2025',
    knowsAbout: [
      'Jamaica jobs', 'job search Jamaica', 'job portal Jamaica',
      'recruitment Jamaica', 'hiring Jamaica', 'employment Jamaica',
      'jobs in Kingston', 'jobs in Montego Bay',
    ],
    areaServed: { '@type': 'Country', name: 'Jamaica' },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      url: 'https://hiremeja.com/contact',
      areaServed: 'JM',
      availableLanguage: 'English',
    },
    sameAs: [
      'https://facebook.com/hireme',
      'https://instagram.com/hireme',
      'https://linkedin.com/company/hireme',
    ],
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'HireMeJA',
    url: 'https://hiremeja.com',
    description: "Jamaica's premier job portal — find jobs in Kingston, Montego Bay, and across all 14 parishes.",
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: 'https://hiremeja.com/jobs?q={search_term_string}',
      },
      'query-input': 'required name=search_term_string',
    },
  };

  const jobBoardSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Find Jobs in Jamaica & Hire Talent | HireMeJA',
    description: "Search Jamaica jobs across all 14 parishes. HireMeJA is Jamaica's premier job portal connecting employers and candidates in Kingston, Montego Bay, and beyond.",
    url: 'https://hiremeja.com',
    primaryImageOfPage: 'https://hiremeja.com/logo512.png',
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: ['h1', '.hero-description'],
    },
  };

  const avgRating = testimonials.length > 0
    ? testimonials.reduce((sum, t) => sum + (t.rating || 0), 0) / testimonials.length
    : null;

  const aggregateRatingSchema = avgRating ? {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'HireMeJA',
    url: 'https://hiremeja.com',
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: avgRating.toFixed(1),
      reviewCount: String(testimonials.length),
      bestRating: '5',
      worstRating: '1',
    },
  } : null;

  const HOME_FAQS = [
    {
      q: 'How do I find a role here?',
      a: "The easiest way is to search HireMeJA — the island's dedicated job portal. Browse by parish (Kingston, Montego Bay, and all 14 parishes), industry, or employment type. Create a free candidate profile to apply directly and receive alerts for new openings that match your skills.",
    },
    {
      q: 'What roles are in highest demand right now?',
      a: 'The most sought-after positions include BPO and customer service agents, nurses and healthcare workers, IT and software professionals, teachers, hotel and hospitality staff, security guards, and skilled tradespeople such as electricians and plumbers. The public sector and Caribbean development organisations also regularly advertise officer and administrative vacancies.',
    },
    {
      q: 'What do most positions pay?',
      a: 'Compensation varies by industry, role, and experience. Entry-level office and customer service positions typically range from JMD 60,000–100,000 per month. BPO and financial services professionals earn JMD 80,000–200,000+. IT, marketing, and business development careers command JMD 150,000–400,000 monthly. Senior officer and project management roles at international organisations can exceed JMD 500,000 per month. Visit our Salary Guide for a full breakdown.',
    },
    {
      q: 'Where are most vacancies located?',
      a: 'The majority of openings are in Kingston and Saint Andrew (the commercial and government capital), Montego Bay in Saint James (the tourism and BPO hub), and Saint Catherine including Portmore. HireMeJA lists opportunities across all 14 parishes.',
    },
    {
      q: 'Do I need a work permit?',
      a: 'Citizens and permanent residents of Jamaica do not need a work permit. Foreign nationals generally require one issued by the Ministry of Labour and Social Security. CARICOM nationals may qualify for exemptions under the CARICOM Single Market and Economy (CSME) agreement.',
    },
    {
      q: 'How do employers post a vacancy?',
      a: 'Register on HireMeJA and select a subscription package. Packages include access to the candidate database, listing slots, and direct applications. Listings are reviewed and go live within 24 hours of submission.',
    },
    {
      q: 'Can a foreign national work here?',
      a: 'Yes. U.S. citizens and other foreign nationals can work here with a valid work permit issued by the Ministry of Labour and Social Security. Many international organisations, the U.S. Embassy, and multinational businesses actively recruit overseas professionals for specialised officer and project roles. CARICOM nationals may apply for a Skills Certificate under the CSME agreement, streamlining the process across Caribbean member states.',
    },
    {
      q: 'What is the minimum wage?',
      a: 'The national minimum wage is reviewed periodically by the Ministry of Labour. As of 2024, the rate for general workers is JMD 15,000 per week. Security officers and other designated categories receive a higher rate. Employers are legally required to meet or exceed these terms for all staff — whether on a full-time, part-time, or contract basis.',
    },
  ];

  const homeFaqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: HOME_FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  return (
    <>
    <Helmet>
      <title>Find Jobs in Jamaica &amp; the Caribbean | HireMeJA</title>
      <meta name="description" content="Search Jamaica jobs in Kingston, Montego Bay, and all 14 parishes. Post jobs or find your next hire on Jamaica's premier job portal — HireMeJA." />
      <meta name="keywords" content="Jamaica jobs, job search Jamaica, job portal Jamaica, jobs in Jamaica, hiring Jamaica, employment Jamaica, find jobs Kingston, Montego Bay jobs" />
      <link rel="canonical" href="https://hiremeja.com/" />

      <meta property="og:title" content="Find Jobs in Jamaica & Hire Talent from Across All 14 Parishes | HireMeJA" />
      <meta property="og:description" content="Jamaica's premier job portal. Find jobs in Kingston, Montego Bay, and across all parishes. Post a job and start hiring today." />
      <meta property="og:url" content="https://hiremeja.com" />
      <meta property="og:type" content="website" />
      <meta property="og:image" content="https://hiremeja.com/logo512.png" />
      <meta property="og:site_name" content="HireMeJA" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Find Jobs in Jamaica & Hire Talent | HireMeJA" />
      <meta name="twitter:description" content="Jamaica's premier job portal. Search thousands of Jamaica jobs today." />
      <meta name="twitter:image" content="https://hiremeja.com/logo512.png" />

      <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(jobBoardSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(homeFaqSchema)}</script>
      {aggregateRatingSchema && (
        <script type="application/ld+json">{JSON.stringify(aggregateRatingSchema)}</script>
      )}
    </Helmet>
    <div className="p-0 m-0">
      {/* Hero Section */}
      <header
        className="max-w-full text-white bg-center bg-cover bg-fixed relative"
        style={{
          backgroundImage: `url(${homeBanner})`,
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
        }}
      >
        <div className="py-20 sm:py-32 md:py-48 bg-black bg-opacity-50">
          <div className="container px-4 mx-auto">
            <div className="grid grid-cols-12">
              <div className="col-span-12 text-center">
                <div className="w-full md:w-3/4 mx-auto px-4">
                  <h1 className="text-2xl sm:text-4xl md:text-5xl text-center font-bold"
                      style={{ color: "#cddd3a", lineHeight: "1.2" }}>
                    Find Jobs in Jamaica &amp; Hire Talent from Across All 14 Parishes
                  </h1>
                  <p className="hero-description text-base sm:text-lg text-white mt-4 opacity-90 max-w-2xl mx-auto">
                    Jamaica's premier job portal connecting employers and job seekers in Kingston, Montego Bay, Ocho Rios, and beyond.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8 md:mt-10">
                    <button 
                      onClick={handleGetHiredClick}
                      className="uppercase hover:scale-95 transition-all bg-[#cddd3a] px-6 py-3 rounded shadow-sm text-blue-950 font-bold w-full sm:w-auto">
                      Get Hired
                    </button>
                    <button 
                      onClick={handleFindTalentClick}
                      className="uppercase hover:scale-95 transition-all bg-white px-6 py-3 rounded shadow-sm text-blue-950 font-bold w-full sm:w-auto">
                      Find Talent
                    </button>
                    <button
                      onClick={handleTemporaryWorkersClick}
                      className="uppercase hover:scale-95 transition-all bg-blue-600 px-6 py-3 rounded shadow-sm text-white font-bold w-full sm:w-auto"
                    >
                      Temporary Workers
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Latest Jobs in Jamaica */}
      {latestJobs.length > 0 && (
        <section className="py-12 sm:py-16 bg-gray-50">
          <div className="container px-4 mx-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl sm:text-3xl font-bold" style={{ color: '#1e3a5f' }}>
                Latest Jobs in Jamaica
              </h2>
              <Link
                to="/jobs-in-jamaica"
                className="flex items-center gap-2 text-sm font-semibold text-blue-950 hover:text-blue-700 transition-colors"
              >
                View all <FaArrowRight size={12} />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {latestJobs.map((job) => {
                const posted = job.approvedAt
                  ? new Date(job.approvedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                  : null;
                return (
                  <Link
                    key={job._key}
                    to={`/jobs/${job.slug || job._key}`}
                    className="block bg-white border border-gray-200 rounded-xl p-5 hover:border-blue-950 hover:shadow-md transition-all group"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                        <FaBuilding className="text-blue-950 w-5 h-5" />
                      </div>
                      {job.employmentType && (
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-[#cddd3a]/20 text-blue-950 shrink-0">
                          {job.employmentType}
                        </span>
                      )}
                    </div>
                    <h3 className="font-bold text-blue-950 mt-3 text-sm sm:text-base group-hover:text-blue-700 transition-colors line-clamp-2">
                      {job.jobTitle}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">{job.companyName}</p>
                    <div className="flex flex-wrap gap-3 mt-3">
                      {job.parish && (
                        <span className="flex items-center gap-1 text-xs text-gray-500">
                          <FaMapMarkerAlt size={10} /> {job.parish}
                        </span>
                      )}
                      {posted && (
                        <span className="flex items-center gap-1 text-xs text-gray-400">
                          <FaClock size={10} /> {posted}
                        </span>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
            <div className="text-center mt-8">
              <Link
                to="/jobs"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-blue-950 text-white text-sm font-semibold hover:bg-blue-900 transition-colors"
              >
                Browse All Jobs <FaArrowRight size={12} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* How It Works Section */}
      <div className="max-w-full mx-auto py-12 sm:py-16 md:py-24 px-4 text-left">
        <h2 className="font-extrabold text-2xl sm:text-3xl md:text-4xl mb-6"
            style={{ color: "#263571" }}>
          How Jamaica's Job Portal Works
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          {/* Left Column - Text Content */}
          <Box className="flex-1 space-y-8">
            {steps.map((step, index) => (
              <Box key={step.id} className="flex items-start relative">
                {index !== steps.length - 1 && (
                  <div style={{ borderColor: "#cddd3a" }} 
                       className="absolute left-10 top-10 h-[calc(100%+2rem)] border-l-4 transform -translate-x-1/2 z-0">
                  </div>
                )}
                <Box className="flex-shrink-0 w-20 h-20 rounded-full flex items-center justify-center text-white font-extrabold text-3xl relative z-10" 
                     style={{ background: "#263571", color: "#cddd3a" }}>
                  {step.id}
                </Box>
                <Box className="ml-4">
                  <Typography variant="h4" className="font-extrabold text-xl" style={{ color: "#263571" }}>
                    {step.title.split(step.highlight).map((part, i, arr) => (
                      <React.Fragment key={i}>
                        {part}
                        {i < arr.length - 1 && <span style={{ color: "#cddd3a" }}>{step.highlight}</span>}
                      </React.Fragment>
                    ))}
                  </Typography>
                  <Typography className="mt-4 text-blue-900">
                    {step.description}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          {/* Right Column - Images */}
          <div className="space-y-6 lg:sticky lg:top-24">
            <img
              src={howHireMeWorks}
              alt="How HireMeJA works — Jamaica's online job portal for employers and job seekers across all 14 parishes"
              loading="lazy"
              width="600"
              height="450"
              className="w-full object-cover hover:scale-105 transition-transform duration-300"
            />
            {/* <img 
              src={howHireMeWorks1} 
              alt="How HireMe Works Additional" 
              className="w-full object-cover hover:scale-105 transition-transform duration-300"
            /> */}
          </div>
        </div>
      </div>

      {/* What We Do Section */}
      <section className="py-14 md:py-24 bg-blue-50 text-black">
        <div className="container px-4 mx-auto">
          <div className="grid grid-cols-12 mb-12">
            <div className="col-span-12 lg:col-span-5">
              <h2 style={{ color: "#263571" }} className="text-[25px] md:text-[45px] leading-none font-bold mb-6">
                Why Jamaica's Employers &amp; Job Seekers Choose HireMe
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-12 gap-y-12 md:gap-x-6">
            {whatwedo.map((feature, i) => (
              <div className="col-span-12 md:col-span-4" key={i}>
                <FeatureItem feature={feature} />
              </div>
            ))}
          </div>
        </div>
        </section>

{/* Testimonials Section */}
<section style={{ background: "#fdffb3" }} className="py-12 sm:py-16 md:py-24">
  <div className="container px-4 mx-auto">
    <div className="text-center mb-12">
      <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-blue-900">
        Reviews
      </h2>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 md:gap-8 pt-10">
      {testimonials.map((testimonial, i) => (
        <div key={i} className="relative pt-10">
          <div className="bg-blue-950 shadow-lg rounded-xl h-full p-6 transition-transform hover:scale-105 duration-300">
            <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 w-20 h-20 
                          bg-blue-950 border rounded-full flex items-center justify-center">
              {testimonial.userType === 'employer' ? (
                <FaBuilding className="text-[#cddd3a] w-10 h-10" />
              ) : (
                <FaUserTie className="text-[#cddd3a] w-10 h-10" />
              )}
            </div>
            <div className="pt-8 text-center">
  <h4 className="text-xl font-bold text-white mt-4">
    {testimonial.isAnonymous ? 'Anonymous' : `${testimonial.firstName} ${testimonial.lastName}`}
  </h4>
  {!testimonial.isAnonymous && testimonial.companyName && testimonial.userType === 'employer' && (
    <p className="text-lg font-bold text-[#cddd3a] mt-2">
      {testimonial.companyName}
    </p>
  )}
  <p className="text-sm text-gray-300 mt-1">
    {testimonial.userType === 'employer' ? 'Employer' : 'Job Seeker'}
  </p>
  <div className="mt-4">
    <p className={`text-base font-medium text-white italic ${
      !expandedReviews.has(testimonial.id) ? 'line-clamp-3' : ''
    }`}>
      {testimonial.comment}
    </p>
    {testimonial.comment && testimonial.comment.length > 150 && (
      <button
        onClick={() => toggleReviewExpansion(testimonial.id)}
        className="text-[#cddd3a] text-sm mt-2 hover:underline transition-colors"
      >
        {expandedReviews.has(testimonial.id) ? 'Show Less' : 'Read More'}
      </button>
    )}
  </div>
  <div className="mt-4">
    <Rating rating={testimonial.rating} />
  </div>
</div>
          </div>
        </div>
      ))}
    </div>
  
    <div className="w-full flex flex-col sm:flex-row justify-center gap-4 mt-12">
      <Button
        variant="contained"
        onClick={() => setIsModalOpen(true)}
        className="w-full sm:w-auto bg-blue-950 hover:bg-blue-900 text-white"
      >
        Submit a review
      </Button>
    </div>
  </div>
</section>

{/* Frequently Asked Questions */}
<section className="py-14 sm:py-20 bg-gray-50">
  <div className="container px-4 mx-auto max-w-3xl">
    <div className="text-center mb-12">
      <h2 className="text-2xl sm:text-3xl font-bold mb-3" style={{ color: '#1e3a5f' }}>
        Frequently Asked Questions
      </h2>
      <p className="text-gray-500 text-sm sm:text-base">
        Everything you need to know about finding jobs in Jamaica
      </p>
    </div>
    <div className="space-y-3">
      {HOME_FAQS.map(({ q, a }) => (
        <details key={q} className="group bg-white border border-gray-200 rounded-xl overflow-hidden">
          <summary className="flex items-center justify-between gap-4 px-6 py-4 cursor-pointer list-none font-semibold text-blue-950 text-sm sm:text-base select-none">
            {q}
            <span className="shrink-0 text-[#cddd3a] text-xl font-bold group-open:rotate-45 transition-transform duration-200">+</span>
          </summary>
          <p className="px-6 pb-5 pt-1 text-sm text-gray-600 leading-relaxed border-t border-gray-100">
            {a}
          </p>
        </details>
      ))}
    </div>
  </div>
</section>

<section className="py-12 sm:py-16 bg-white border-t border-gray-100">
  <div className="container px-4 mx-auto">
    <h2 className="text-xl sm:text-2xl font-bold mb-8" style={{ color: '#1e3a5f' }}>
      Explore Career Opportunities in Jamaica &amp; the Caribbean
    </h2>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">
      {[
        { label: 'Career Opportunities in Jamaica',           to: '/jobs-in-jamaica' },
        { label: 'Customer Service & BPO Careers',            to: '/jobs/industry/bpo' },
        { label: 'Financial Services & Banking',              to: '/jobs/industry/banking' },
        { label: 'Marketing & Business Development',          to: '/jobs/industry/marketing' },
        { label: 'International & Caribbean Careers',         to: '/jobs-in-jamaica' },
        { label: 'Part-Time & Contract Positions',            to: '/jobs/type/part-time' },
        { label: 'Office & Administrative Officer Roles',     to: '/jobs/specialty/administrative' },
        { label: 'Open Vacancies — All 14 Parishes',         to: '/jobs' },
        { label: 'Project Management Careers',               to: '/jobs/specialty/administrative' },
        { label: 'IT & Software Development',                to: '/jobs/industry/technology' },
        { label: "Jamaica's #1 Career Portal",               to: '/jobs-in-jamaica' },
        { label: 'U.S. & International Employers in Jamaica', to: '/jobs' },
      ].map(({ label, to }) => (
        <Link
          key={label}
          to={to}
          className="flex items-center gap-2 text-sm text-blue-950 hover:text-blue-600 transition-colors py-1"
        >
          <span className="text-[#cddd3a] font-bold">›</span>
          {label}
        </Link>
      ))}
    </div>
  </div>
</section>

{/* Review Modal */}
<Modal
  open={isModalOpen}
  onClose={() => setIsModalOpen(false)}
  aria-labelledby="review-modal-title"
>
  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 
              w-11/12 max-w-md bg-white rounded-lg shadow-xl p-6">
    <h2 className="text-2xl font-bold text-blue-950 mb-6" id="review-modal-title">
      Submit Your Review
    </h2>
    
    {!auth.currentUser ? (
      <div className="text-center">
        <p className="text-gray-600 mb-4">Please sign in to submit a review</p>
        <Button
          onClick={() => setIsModalOpen(false)}
          variant="contained"
          className="bg-blue-950 hover:bg-blue-900 text-white"
        >
          Got it
        </Button>
      </div>
    ) : !userType ? (
      <div className="text-center">
        <p className="text-gray-600 mb-4">Please complete your profile to submit a review</p>
        <Button
          onClick={() => setIsModalOpen(false)}
          variant="contained"
          className="bg-blue-950 hover:bg-blue-900 text-white"
        >
          Got it
        </Button>
      </div>
    ) : (
      <>
        <div className="mb-6">
          <p className="text-sm text-gray-600 mb-2">Submitting as: {userType === 'employer' ? 'Employer' : 'Job Seeker'}</p>
          <label className="block text-gray-700 mb-2">Your Rating</label>
          <MuiRating
            value={reviewRating}
            onChange={(_, newValue) => setReviewRating(newValue)}
            size="large"
            className="text-blue-950"
          />
        </div>
        
        <div className="mb-6">
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Your Review"
            value={reviewComment}
            onChange={(e) => setReviewComment(e.target.value)}
            placeholder="Share your experience..."
            className="bg-white"
          />
        </div>

        <div className="mb-6">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={isAnonymous}
              onChange={(e) => setIsAnonymous(e.target.checked)}
              className="w-4 h-4 text-blue-950 border-gray-300 rounded focus:ring-blue-950"
            />
            <span className="ml-2 text-gray-700">Post anonymously</span>
          </label>
          <p className="text-xs text-gray-500 mt-1 ml-6">Your name will not be displayed with this review</p>
        </div>

        <div className="flex justify-end space-x-4">
          <Button
            onClick={() => setIsModalOpen(false)}
            variant="outlined"
            className="text-blue-950 border-blue-950 hover:border-blue-900"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmitReview}
            variant="contained"
            className="bg-blue-950 hover:bg-blue-900 text-white"
            disabled={!reviewComment || reviewRating === 0}
          >
            Submit Review
          </Button>
        </div>
      </>
    )}
  </div>
</Modal>
</div>
</>
);
};

export default Home;  